import service from '@/api';
import axios from 'axios';
import { appStore } from '@/mobx';
import { getOperatorTokenFromLocalStorage } from '@/utils/token';

export const userLoginByCode = () => service.get('/mock');
export const getUserInfo = () => service.get('/user/info');

export const getOperatorUserInfo = () =>
  service.get('https://api.aiwebagent.cn/api/v1/user/info', {
    headers: {
      Token: getOperatorTokenFromLocalStorage(),
    },
  });

export const getUserMenus = () =>
  service.get('https://mock.mengxuegu.com/api/user/menus');
// 获取应用信息
export const getAppConfig = () =>
  service.get('/user/public/config?key=app_config');

/**
 *
 * 账号登录
 * */
export const userLogin = async (params): Promise<any> =>
  service.post('/user/login/assistant', params);

const OPERATOR_TOKEN = 'kLzvylAuNTeddhrTR35IBNE1Hr34mTre';

const OPERATOR_LOGIN_URL =
  'https://api.aiwebagent.cn/api/v1/customer-operation/operator/login';

const OPERATOR_CUSTOMER_LIST_URL =
  'https://api.aiwebagent.cn/api/v1/customer-operation/operator/customers';

const OPERATOR_GEO_TOKEN_URL =
  'https://api.aiwebagent.cn/api/v1/customer-operation/operator/geo-token';

interface AgentLoginParams {
  phone_number: string;
  password: string;
  remember_me?: boolean;
  is_operator?: boolean;
}

/**
 * 代运营登录接口与代理商登录接口保持统一返回结构，
 * 避免登录 store 感知不同接口的字段差异。
 */
const normalizeOperatorLoginResponse = (response: any) => {
  const data = response?.data || {};
  const userInfo =
    data.user_info || data.user || data.item || data.operator || {};
  const token =
    data.token || data.access_token || data.accessToken || userInfo.token;
  const agentId =
    userInfo.agent_id ?? userInfo.agentId ?? data.agent_id ?? data.agentId;

  return {
    ...response,
    data: {
      ...data,
      token,
      user_info: {
        ...userInfo,
        agent_id: agentId,
      },
    },
  };
};

export const getOperatorCustomerList = async (params: any): Promise<any> => {
  return service.get(OPERATOR_CUSTOMER_LIST_URL, {
    params,
    headers: {
      'operator-token': OPERATOR_TOKEN,
    },
  });
};

export const getOperatorGeoToken = async (teamId: number): Promise<any> => {
  const type = process.env.NODE_ENV === 'production' ? '' : 'dev';
  console.log('type====>', type);

  return service.get(
    `${OPERATOR_GEO_TOKEN_URL}?team_id=${teamId}&type=${type}`,
    {
      headers: {
        'operator-token': OPERATOR_TOKEN,
      },
    }
  );
};

export const agentLogin = async (params: AgentLoginParams): Promise<any> => {
  const { is_operator } = params;
  const loginParams = {
    phone_number: params.phone_number,
    password: params.password,
  };
  if (is_operator) {
    const response = await service.post(OPERATOR_LOGIN_URL, loginParams);
    return normalizeOperatorLoginResponse(response);
  } else {
    const response = await service.post('/crm/user', loginParams);
    return response;
  }
};

export const getUserAvatarUrl = async (params): Promise<any> =>
  service.get(`/space/oss/url?oss_key=${params}`);

export const preAuth = async (params): Promise<any> =>
  service.post('/geo/auth/pre?team_id=' + params.team_id, params);

export const getAuthList = async (params): Promise<any> =>
  service.get('/geo/auth', { params });

let activeOperatorCountRequests = 0;
const operatorCountQueue: Array<() => void> = [];

export const getOperatorCustomerExpiredCount = async (
  teamId: number,
  platformId: number,
  signal: AbortSignal
): Promise<number> => {
  await new Promise<void>((resolve) => {
    const start = () => {
      activeOperatorCountRequests += 1;
      resolve();
    };
    if (activeOperatorCountRequests < 3) start();
    else operatorCountQueue.push(start);
  });

  let stage = '准备统计';
  try {
    if (signal.aborted) throw new Error('统计查询已取消');
    if (!teamId) throw new Error('客户 team_id 无效');

    // 使用独立客户端，避免全局拦截器覆盖客户凭证或因单个客户失效而退出登录。
    const client = axios.create({
      baseURL: service.defaults.baseURL,
      timeout: service.defaults.timeout,
      headers: { Aid: String(appStore.aid || 0) },
    });
    stage = '获取客户授权信息';
    const { data: tokenResponse } = await client.get(OPERATOR_GEO_TOKEN_URL, {
      params: {
        team_id: teamId,
        type: process.env.NODE_ENV === 'production' ? '' : 'dev',
      },
      headers: {
        'operator-token': OPERATOR_TOKEN,
        Token: getOperatorTokenFromLocalStorage(),
      },
      signal,
    });
    if (tokenResponse.code != 0) {
      throw new Error(`接口返回错误（${tokenResponse.code}）`);
    }
    const tokenData = tokenResponse.data;
    const token =
      (typeof tokenData === 'string' ? tokenData : tokenData?.token) ||
      tokenData?.geo_token ||
      tokenResponse.token;
    if (!token) throw new Error('接口未返回客户凭证');

    stage = '查询客户授权账号';
    const { data: response } = await client.get('/geo/auth', {
      params: { team_id: teamId, platform_id: platformId, account_type: 1 },
      headers: { Token: token },
      signal,
    });
    if (response.code != 0) {
      throw new Error(`接口返回错误（${response.code}）`);
    }
    const items = response.data?.items ?? [];
    if (!Array.isArray(items)) {
      throw new Error('接口返回的账号列表格式错误');
    }
    return items.filter(
      (item: { auth_status: number | string }) => Number(item.auth_status) === 2
    ).length;
  } catch (error) {
    if (signal.aborted) throw error;
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;
    const detail = status
      ? `HTTP ${status}`
      : error instanceof Error
      ? error.message
      : '未知错误';
    throw new Error(`${stage}失败：${detail}`);
  } finally {
    activeOperatorCountRequests -= 1;
    operatorCountQueue.shift()?.();
  }
};

export const deleteAuth = async (id): Promise<any> =>
  service.delete(`/geo/auth/${id}`);

export const updateAuth = async (id, params): Promise<any> =>
  service.patch(`/geo/auth/${id}`, params);
// 抖音城市选择
export const getCityData = async (): Promise<any> =>
  service.get(`/video/poi/city/data`);
// 编辑城市
export const editAuthCity = async (id: number, data: any): Promise<any> =>
  service.put(`/geo/auth/${id}/area`, data);

export const getAgentMembers = async (params?: any): Promise<any> =>
  service.get('/crm/member', { params });

// 获取小助手客户列表及所选平台的失效账号数量
export const getAssistantAgentMembers = async (params?: any): Promise<any> =>
  service.get('/crm/member/assistant', { params });

export const getAgentInfo = async (): Promise<any> =>
  service.get('/crm/user/info');

export const reportTask = async (teamId, data: any): Promise<any> =>
  service.put(
    teamId
      ? `/geo/article/publish/status?team_id=${teamId}`
      : `/geo/article/publish/status`,
    data
  );
